/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useParams, useNavigate } from 'react-router-dom';
import { Bid } from '../../types';
import { apiClient } from '../../services/api';
import './ExecutiveViewBid.css';

const ExecutiveViewBid: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bid, setBid] = useState<Bid | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionNotes, setSubmissionNotes] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchBid();
    }
  }, [id]);

  const fetchBid = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getBidById(parseInt(id!));

      if (response.success && response.bid) {
        setBid(response.bid);
        setSubmissionNotes(response.bid.submissionNotes || '');
      } else {
        setError(response.error || 'Failed to load bid');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while fetching the bid');
      console.error('Error fetching bid:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!bid) return;

    try {
      setSaving(true);
      const response = await apiClient.updateBid(bid.id, {
        submissionNotes: submissionNotes
      });

      if (response.success) {
        alert('Submission notes saved successfully');
        fetchBid(); // Reload to get updated data
      } else {
        alert('Failed to save: ' + (response.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Error saving: ' + (err.response?.data?.error || err.message));
      console.error('Error saving submission notes:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'No date set';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="executive-view-bid">
      <BackButton />
        <div className="loading">Loading bid...</div>
      </div>
    );
  }

  if (error || !bid) {
    return (
      <div className="executive-view-bid">
        <div className="error">{error || 'Bid not found'}</div>
        <button onClick={() => navigate(-1)} className="back-button">Go Back</button>
      </div>
    );
  }

  return (
    <div className="executive-view-bid">
      <div className="bid-header">
        <button onClick={() => navigate(-1)} className="back-button">
          &lt; Back
        </button>
        <h1>{bid.name}</h1>
        <div className="bid-status-badges">
          {bid.submitted && <span className="status-badge submitted">Submitted</span>}
          {bid.accepted && <span className="status-badge accepted">Accepted</span>}
        </div>
      </div>

      <div className="bid-sections">
        <div className="bid-section">
          <h2>Bid Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Author:</label>
              <span>{bid.author ? `${bid.author.firstName} ${bid.author.lastName}` : 'Unknown'}</span>
            </div>
            <div className="info-item">
              <label>Due Date:</label>
              <span>{formatDate(bid.dueDate)}</span>
            </div>
            <div className="info-item">
              <label>Price:</label>
              <span className="price">{formatCurrency(bid.price)}</span>
            </div>
            <div className="info-item">
              <label>Margin:</label>
              <span>{bid.margin ? `${bid.margin}%` : 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Created:</label>
              <span>{formatDate(bid.createdDate)}</span>
            </div>
            <div className="info-item">
              <label>Last Modified:</label>
              <span>{formatDate(bid.lastModified)}</span>
            </div>
            {bid.submittedDate && (
              <div className="info-item">
                <label>Submitted Date:</label>
                <span>{formatDate(bid.submittedDate)}</span>
              </div>
            )}
            {bid.acceptedDate && (
              <div className="info-item">
                <label>Accepted Date:</label>
                <span>{formatDate(bid.acceptedDate)}</span>
              </div>
            )}
          </div>
        </div>

        {bid.internalNotes && (
          <div className="bid-section">
            <h2>Internal Notes</h2>
            <div className="read-only-content">{bid.internalNotes}</div>
          </div>
        )}

        <div className="bid-section editable-section">
          <h2>Submission Notes <span className="editable-badge">EDITABLE</span></h2>
          <textarea
            className="submission-notes-input"
            value={submissionNotes}
            onChange={(e) => setSubmissionNotes(e.target.value)}
            placeholder="Enter submission notes..."
            rows={6}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save Submission Notes'}
          </button>
        </div>

        {bid.bidBodyCount && bid.bidBodyCount > 0 && (
          <div className="bid-section">
            <h2>Bid Bodies</h2>
            <p className="info-text">This bid contains {bid.bidBodyCount} bid bod{bid.bidBodyCount === 1 ? 'y' : 'ies'}.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveViewBid;
